<template>
  <div class="mt-4 full-height" :class="isEcomV2 ? 'smaller-container-wrapper' : ''">
    <div class="mb-4">
      <h5 v-if="vehicleDetails && !isEcomV2" class="font-weight-bold">{{ vehicleDetails }}</h5>
      <h2 class="font-weight-bold mt-4 fs-5 fs-md-4 fs-lg-3">
        {{ $t('message.trade.carFaxTradeInDetailsTitle') }}
      </h2>
      <p class="carfax-msg mt-3">{{ $t('message.trade.carFaxVehicleMoreDetailsMsg') }}</p>
    </div>
    <MobialisFormWithValidation
      @onValidate="submit"
      novalidate
      v-av-analytics-form-submit="{ elementText: $t('message.common.continue') }"
    >
      <div class="row form-row">
        <div class="col-sm-12 mb-2">
          <auto-complete
            :key="`${trimOptions}`"
            rules="required"
            :label="$t('message.trade.vehicleInfo.trim')"
            :placeholder="$t('message.trade.vehicleInfo.selectTrim')"
            :hiddenLabel="false"
            :value="trim"
            :sourceData="trimOptions"
            @onSelect="handleTrimSelection"
            id="trimId"
            vid="trimId"
            data-cy="trim"
          >
          </auto-complete>
        </div>
        <div class="col-sm-12 mb-2">
          <auto-complete
            :key="`${formattedEngineOptions}`"
            rules="required"
            :label="$t('message.trade.vehicleInfo.engine')"
            :placeholder="$t('message.trade.vehicleInfo.selectEngine')"
            :hiddenLabel="false"
            :value="engine"
            :sourceData="formattedEngineOptions"
            :disabled="formattedEngineOptions.length === 0"
            @onSelect="handleEngineSelection"
            id="engineId"
            vid="engineId"
            data-cy="engine"
          >
          </auto-complete>
        </div>
        <div class="col-sm-12 mb-2">
          <auto-complete
            :key="`${formattedDrivetrainOptions}`"
            rules="required"
            :label="$t('message.trade.vehicleInfo.drivetrain')"
            :placeholder="$t('message.trade.vehicleInfo.selectDrivetrain')"
            :hiddenLabel="false"
            :value="drivetrain"
            :sourceData="formattedDrivetrainOptions"
            :disabled="formattedDrivetrainOptions.length === 0"
            @onSelect="handleDrivetrainSelection"
            id="drivetrainId"
            vid="drivetrainId"
            data-cy="drivetrain"
          >
          </auto-complete>
        </div>
        <div class="col-sm-12 mb-2">
          <auto-complete
            :key="`${formattedTransmissionOptions}`"
            rules="required"
            :label="$t('message.trade.vehicleInfo.transmission')"
            :placeholder="$t('message.trade.vehicleInfo.selectTransmission')"
            :hiddenLabel="false"
            :value="transmission"
            :sourceData="formattedTransmissionOptions"
            :disabled="formattedTransmissionOptions.length === 0"
            @onSelect="handleTransmissionSelection"
            id="transmissionId"
            vid="transmissionId"
            data-cy="transmission"
          >
          </auto-complete>
        </div>
        <div class="col-sm-12 mb-2">
          <auto-complete
            :key="`${formattedBodyStyles}`"
            rules="required"
            :label="$t('message.trade.vehicleInfo.bodystyle')"
            :placeholder="$t('message.trade.vehicleInfo.selectBodystyle')"
            :hiddenLabel="false"
            :value="bodyStyle"
            :sourceData="formattedBodyStyles"
            :disabled="formattedBodyStyles.length === 0"
            @onSelect="handleBodyStyleSelection"
            id="bodystyleId"
            vid="bodystyleId"
            data-cy="bodystyle"
          >
          </auto-complete>
        </div>
        <div class="col-sm-12" v-if="boxLengthOptions && boxLengthOptions.length > 0">
          <auto-complete
            :key="`${boxLengthOptions}`"
            rules="required"
            :label="$t('message.trade.vehicleInfo.boxLength')"
            :placeholder="$t('message.trade.vehicleInfo.selectBoxLength')"
            :hiddenLabel="false"
            :value="boxLength"
            :sourceData="boxLengthOptions"
            :disabled="boxLengthOptions.length === 0"
            @onSelect="handleBoxLengthSelection"
            id="boxLengthId"
            vid="boxLengthId"
            data-cy="boxLength"
          >
          </auto-complete>
        </div>
      </div>
      <div class="push-down-desktop">
        <mobialis-button type="submit" class="next-btn mt-5 mb-3" data-cy="continue">
          {{ $t('message.common.continue') }}
        </mobialis-button>
        <mobialis-button
          v-if="currentSubFlow !== 'trade-in' && isEcomV2"
          variant="link"
          @click="skipTrade"
          data-cy="skipTrade"
          v-av-analytics-cta-interaction="{
            elementText: $t('message.eCommerceV2.tradeInFlow.skipTrade'),
            eventActionResult: 'open',
          }"
        >
          {{ $t('message.eCommerceV2.tradeInFlow.skipTrade') }}
        </mobialis-button>
      </div>
    </MobialisFormWithValidation>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import log from '@/services/log';
import { AVRProducts } from '@/utils/enums';
import AutoComplete from '@/components/shared/AutoComplete.vue';

const dependencies = {
  trim: ['engine', 'drivetrain', 'transmission', 'bodyStyle', 'boxLength'],
  engine: ['drivetrain', 'transmission', 'bodyStyle', 'boxLength'],
  drivetrain: ['bodyStyle', 'transmission', 'boxLength'],
  transmission: ['bodyStyle', 'boxLength'],
  bodyStyle: ['boxLength'],
  boxLength: [],
};

export default {
  name: 'CarfaxDetailsForm',
  components: { AutoComplete },
  data() {
    return {
      hasBalanceOnVehicle: this.$store.state.trade.hasBalanceOnVehicle,
    };
  },
  computed: {
    ...mapState({
      year: state => state.trade.vehicle.year,
      make: state => state.trade.vehicle.make,
      model: state => state.trade.vehicle.model,
      trim: state => state.trade.vehicle.trim,
      engine: state => state.trade.vehicle.engine,
      drivetrain: state => state.trade.vehicle.drivetrain,
      transmission: state => state.trade.vehicle.transmission,
      bodyStyle: state => state.trade.vehicle.bodyStyle,
      boxLength: state => state.trade.vehicle.boxLength,
      vehicle: state => state.trade.vehicle,
    }),

    ...mapGetters({
      trimOptions: 'trade/getTrimOptions',
      engineOptions: 'trade/getEngineOptions',
      vehicleDetails: 'trade/getVehicleName',
      getCurrentProduct: 'getCurrentProduct',
      getProductHomeState: 'getProductHomeState',
    }),

    formattedEngineOptions() {
      return this.engineOptions.map(option => ({
        ...option,
        value: option.engineLabel,
        label: option.engineLabel,
      }));
    },

    activatedEngine() {
      return this.engineOptions.find(option => option.engineLabel === this.engine);
    },

    formattedDrivetrainOptions() {
      if (!this.activatedEngine && !this.activatedEngine?.drivetrainOptions) {
        return [];
      }

      return this.activatedEngine?.drivetrainOptions.map(option => ({
        ...option,
        value: option.driveTrain,
        label: option.driveTrain,
      }));
    },

    activatedDrivetrain() {
      return this.formattedDrivetrainOptions.find(option => option.driveTrain === this.drivetrain);
    },

    formattedTransmissionOptions() {
      return this.activatedDrivetrain && this.activatedDrivetrain.transmissionOptions
        ? this.activatedDrivetrain.transmissionOptions
        : [];
    },

    formattedBodyStyles() {
      if (!this.activatedDrivetrain && !this.activatedDrivetrain?.bodyStyleOptions) {
        return [];
      }

      const bodyStyleArray = Array.isArray(this.activatedDrivetrain.bodyStyleOptions)
        ? this.activatedDrivetrain.bodyStyleOptions
        : [this.activatedDrivetrain.bodyStyleOptions];

      return bodyStyleArray.map(option => ({
        ...option,
        value: option.bodyStyle,
        label: option.bodyStyle,
      }));
    },

    activatedBodyStyle() {
      return this.formattedBodyStyles.find(option => option.bodyStyle === this.bodyStyle);
    },

    boxLengthOptions() {
      return this.activatedBodyStyle && this.activatedBodyStyle.boxLengthOptions
        ? this.activatedBodyStyle.boxLengthOptions
        : [];
    },

    isCredit() {
      return this.getCurrentProduct === AVRProducts.CREDIT;
    },
    isEcomV2() {
      return this.getCurrentProduct === AVRProducts.ECOMV2;
    },
    isAcquire() {
      return this.getCurrentProduct === AVRProducts.ACQUIRE;
    },
  },
  async created() {
    const { year: routeYear, make: routeMake, model: routeModel } = this.$route.params;
    const isFirstLoad = !this.year && !this.make && !this.model;
    if (isFirstLoad && routeYear && routeMake && routeModel) {
      this.setVehicle({
        year: decodeURIComponent(routeYear),
        make: decodeURIComponent(routeMake),
        model: decodeURIComponent(routeModel),
      });
    }
    // check if the YMM vehicle is already set if not redirect to the previous page
    if (this.year && this.make && this.model) {
      try {
        await this.getTrims();
      } catch (error) {
        this.handleSelectionError('Selection Trim', error);
      }
    } else {
      this.$router.push({ path: 'carfax-basic-form' });
    }
  },
  methods: {
    ...mapActions('trade', {
      getEngine: 'getEngine',
      getTrims: 'getTrims',
    }),

    ...mapMutations(['trade/SET_VEHICLE']),
    ...mapMutations('trade', {
      setTrim: 'SET_VEHICLE_TRIM',
      setVehicle: 'SET_VEHICLE',
      setVehicleEngine: 'SET_VEHICLE_ENGINE',
      setVehicleFuelType: 'SET_VEHICLE_FUELTYPE',
      setVehicleDrivetrain: 'SET_VEHICLE_DRIVETRAIN',
      setVehicleTransmission: 'SET_VEHICLE_TRANSMISSION',
      setVehicleBodyStyle: 'SET_VEHICLE_BODYSTYLE',
      setVehicleBoxLength: 'SET_VEHICLE_BOXLENGTH',
    }),

    handleTrimSelection(selectedTrim) {
      if (!selectedTrim) return;
      if (this.isPersistedValue(selectedTrim)) return;

      this.setTrim(selectedTrim);
      this.resetSpecificOptions('trim');
      this.fetchEngineOptions().then(() => this.resetSpecificOptions('engine'));
    },

    handleEngineSelection(selectedEngineLabel) {
      if (!selectedEngineLabel) return;
      if (this.isPersistedValue(selectedEngineLabel)) return;

      const selectedactivatedEngine = this.engineOptions.find(option => option.engineLabel === selectedEngineLabel);
      this.setVehicleEngine(selectedactivatedEngine ? selectedactivatedEngine.engineLabel : '');
      this.setVehicleFuelType(selectedactivatedEngine ? selectedactivatedEngine.fuelType : '');

      this.resetSpecificOptions('engine');

      // Select the first drivetrain option if there is only one
      if (this.formattedDrivetrainOptions.length === 1) {
        this.handleDrivetrainSelection(this.formattedDrivetrainOptions[0].driveTrain || '');
      }
    },

    handleDrivetrainSelection(selectedDrivetrain) {
      if (!selectedDrivetrain) return;
      if (this.isPersistedValue(selectedDrivetrain)) return;

      const selectedactivatedDrivetrain = this.formattedDrivetrainOptions.find(
        option => option.driveTrain === selectedDrivetrain
      );

      this.setVehicleDrivetrain(selectedactivatedDrivetrain ? selectedactivatedDrivetrain.driveTrain : '');
      this.resetSpecificOptions('drivetrain');

      // Select the first transmission option if there is only one
      if (this.formattedTransmissionOptions.length === 1) {
        this.handleTransmissionSelection(this.formattedTransmissionOptions[0] || '');
      }
    },

    handleTransmissionSelection(selectedTransmission) {
      if (!selectedTransmission) return;
      if (this.isPersistedValue(selectedTransmission)) return;

      this.setVehicleTransmission(selectedTransmission);
      this.resetSpecificOptions('transmission');

      // Select the first body style option if there is only one
      if (this.formattedBodyStyles.length === 1) {
        this.handleBodyStyleSelection(this.formattedBodyStyles[0].bodyStyle || '');
      }
    },

    handleBodyStyleSelection(selectedBodyStyle) {
      if (!selectedBodyStyle) return;
      if (this.isPersistedValue(selectedBodyStyle)) return;

      const selectedactivatedBodyStyle = this.formattedBodyStyles.find(
        option => option.bodyStyle === selectedBodyStyle
      );
      this.setVehicleBodyStyle(selectedactivatedBodyStyle ? selectedactivatedBodyStyle.bodyStyle : '');
      this.resetSpecificOptions('boxLength');
    },

    handleBoxLengthSelection(selectedBoxLength) {
      if (!selectedBoxLength) return;
      if (this.isPersistedValue(selectedBoxLength)) return;

      this.setVehicleBoxLength(selectedBoxLength);
    },

    async fetchEngineOptions() {
      try {
        await this.getEngine();
      } catch (error) {
        this.handleSelectionError('Selection Engine', error);
      }
    },

    submit(isValid) {
      if (!isValid) {
        return;
      }
      // eslint-disable-next-line no-unused-expressions
      this.isAcquire
        ? this.$store.commit('trade/SET_LEAD_ACQUIRE_VEHICLE')
        : this.$store.commit('trade/SET_LEAD_TRADEIN_VEHICLE');
      if (this.isCredit) {
        this.navigateToCreditPage();
      } else if (this.isEcomV2) {
        this.$router.push({ path: this.getNextRoutePath() });
      } else {
        this.navigateToPersonalFormPage();
      }
    },
    navigateToPersonalFormPage() {
      const trim = encodeURIComponent(this.$store.state.trade.vehicle.trim) || 'no-trim';
      this.$router.push({ path: `${trim}/personal-info-form` });
    },
    resetAll() {
      ['trim', 'engine', 'drivetrain', 'transmission', 'bodyStyle', 'boxLength'].forEach(field => {
        const mutationType = `trade/SET_VEHICLE_${field.toUpperCase()}`;
        if (this.$store._mutations[mutationType]) {
          this.$store.commit(mutationType, '');
        }
        const objectField = `${field}Object`;
        if (Object.hasOwn(this, objectField)) {
          this[objectField] = null;
        }
      });
    },
    resetSpecificOptions(field) {
      const fieldsToReset = dependencies[field] || [];

      fieldsToReset.forEach(dependentField => {
        const mutationType = `trade/SET_VEHICLE_${dependentField.toUpperCase()}`;
        if (this.$store._mutations && this.$store._mutations[mutationType]) {
          this.$store.commit(mutationType, '');
        }

        const objectField = `${dependentField}Object`;
        if (Object.prototype.hasOwnProperty.call(this, objectField)) {
          this[objectField] = null;
        }
      });
    },
    async navigateToCreditPage() {
      if (this.getProductHomeState.trade.state !== this.enums.DHSState.COMPLETE) {
        await this.getTradeReport();
      }
      let nextPage = `credit-results-${this.baseRoute}`;
      if (this.hasBalanceOnVehicle === this.enums.YesNo.YES) {
        nextPage = `select-auto-loan-${this.baseRoute}`;
      }
      this.$router.push({ name: nextPage });
    },
    async getTradeReport() {
      this.$store.commit('SET_LOADING', true);
      try {
        try {
          await this.$store.dispatch('trade/getReport');
        } catch (error) {
          log('Error on getting trade-in report', error);
        }
        try {
          await this.$store.dispatch('lead/updateLead');
        } catch (error) {
          log('Error Updating credit flow', error);
        }
      } finally {
        this.$store.commit('SET_LOADING', false);
      }
    },
    skipTrade() {
      // only visible the personalized payments main flow and make an offer flow
      // reset state related to trade in
      this.$store.commit('trade/RESET_VEHICLE');
      this.$store.commit('trade/SET_LEAD_TRADEIN_VEHICLE');
      this.$store.commit('paymentCalculator/REMOVE_TRADE');
      if (this.ecomFlowSelected === 'personalized-payment') {
        this.$router.push({ path: 'contact-info-form' });
      } else {
        this.$router.push({ path: 'payment-calculator' });
      }
    },

    handleSelectionError(context, error) {
      log(`Error in ${context}`, error);
      this.$router.push({ path: `${this.getProductBasePath()}/error` });
    },

    isPersistedValue(value) {
      return value && typeof value === 'object' && !value.isChanged;
    },
  },
};
</script>
